import React, { useMemo, useState } from "react";
import { Toolbar } from "@mui/x-data-grid";
import {
  Box,
  Button,
  TextField,
  Typography,
} from "@mui/material";

/**
 * Reusable go-to-page controls.
 *
 * Expects:
 * - totalPages: total number of available pages
 * - onPageChange: callback using a 1-based page number
 */
export function GoToPageControls({
  totalPages,
  onPageChange,
}) {
  const [goToPage, setGoToPage] = useState("");

  const safeTotalPages = Math.max(
    1,
    Number(totalPages) || 1
  );

  const goToPlaceholder = `1-${safeTotalPages}`;

  const textFieldWidth = Math.min(
    Math.max(88, 48 + goToPlaceholder.length * 7),
    130
  );

  const parsedPage = Number(goToPage);

  const isValidPage = useMemo(
    () =>
      Number.isInteger(parsedPage) &&
      parsedPage >= 1 &&
      parsedPage <= safeTotalPages,
    [parsedPage, safeTotalPages]
  );

  const handleGoToPage = () => {
    if (!isValidPage) {
      return;
    }

    onPageChange?.(parsedPage);
    setGoToPage("");
  };

  const handlePageInputChange = (event) => {
    const value = event.target.value;

    /*
     * Allow an empty value so the user can clear the field.
     * Otherwise, allow only digits.
     */
    if (value === "" || /^\d+$/.test(value)) {
      setGoToPage(value);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        flexShrink: 0,
        width: "max-content",
        whiteSpace: "nowrap",
      }}
    >
      <Typography
        component="label"
        htmlFor="go-to-page-input"
        variant="body2"
        color="text.secondary"
        sx={{
          whiteSpace: "nowrap",
          lineHeight: 1,
        }}
      >
        Go to page
      </Typography>

      <TextField
        id="go-to-page-input"
        size="small"
        type="text"
        value={goToPage}
        onChange={handlePageInputChange}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            handleGoToPage();
          }
        }}
        placeholder={goToPlaceholder}
        error={goToPage !== "" && !isValidPage}
        slotProps={{
          htmlInput: {
            inputMode: "numeric",
            pattern: "[0-9]*",
            "aria-label": `Page number from 1 to ${safeTotalPages}`,
          },
        }}
        sx={{
          width: textFieldWidth,
          flexShrink: 0,

          "& .MuiInputBase-root": {
            height: 34,
          },

          "& .MuiInputBase-input": {
            px: 1.25,
            py: 0,
            textAlign: "center",
            fontSize: "0.875rem",
          },
        }}
      />

      <Button
        size="small"
        variant="outlined"
        onClick={handleGoToPage}
        disabled={!isValidPage}
        aria-label="Go to selected page"
        sx={{
          height: 34,
          minWidth: 48,
          px: 1.25,
          flexShrink: 0,
        }}
      >
        Go
      </Button>
    </Box>
  );
}

/**
 * Standalone DataGrid toolbar.
 *
 * The DataGrid pagination model uses zero-based page numbers,
 * while GoToPageControls uses one-based page numbers.
 */
function GoToPageToolbar({
  totalPages,
  onPageChange,
}) {
  return (
    <Toolbar
      aria-label="Table pagination toolbar"
      sx={{
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        width: "100%",

        px: 2,
        py: 1,
        minHeight: 52,

        borderBottom: "1px solid",
        borderColor: "divider",

        overflow: "visible",
        flexWrap: "nowrap",
      }}
    >
      <GoToPageControls
        totalPages={totalPages}
        onPageChange={(parsedPage) => {
          /*
           * Convert the control's 1-based page number to
           * the DataGrid's zero-based page number.
           */
          onPageChange?.(parsedPage - 1);
        }}
      />
    </Toolbar>
  );
}

export default GoToPageToolbar;